import React, { useState, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './Customers.css';

export default function Customers() {
  const { orders, loading } = useData();
  const [searchTerm, setSearchTerm] = useState('');

  // Créer un objet unique par client avec leurs commandes groupées
  const customers = useMemo(() => {
    const customerMap = orders.reduce((acc, order) => {
      const phone = order.customer.phone;
      
      if (!acc[phone]) {
        acc[phone] = {
          firstName: order.customer.firstName,
          lastName: order.customer.lastName,
          phone: phone,
          wilaya: order.customer.wilaya,
          city: order.customer.city,
          orders: [],
          totalSpent: 0,
          lastOrderDate: new Date(order.createdAt)
        };
      }

      acc[phone].orders.push(order);
      acc[phone].totalSpent += order.product.subtotal;
      
      // Mettre à jour la date de dernière commande si nécessaire
      const orderDate = new Date(order.createdAt);
      if (orderDate > acc[phone].lastOrderDate) {
        acc[phone].lastOrderDate = orderDate;
      }

      return acc;
    }, {});

    return Object.values(customerMap);
  }, [orders]);

  // Filtrer les clients selon la recherche
  const filteredCustomers = customers.filter(customer => {
    const searchStr = searchTerm.toLowerCase();
    return (
      customer.firstName.toLowerCase().includes(searchStr) ||
      customer.lastName.toLowerCase().includes(searchStr) ||
      customer.phone.includes(searchStr) ||
      customer.wilaya.toLowerCase().includes(searchStr)
    );
  });

  // Statistiques des clients
  const stats = useMemo(() => {
    return {
      totalCustomers: customers.length,
      activeCustomers: customers.filter(c => 
        c.orders.some(o => ['pending', 'confirmed', 'shipped'].includes(o.status))
      ).length,
      totalRevenue: customers.reduce((sum, c) => sum + c.totalSpent, 0),
      averageOrderValue: customers.length 
        ? customers.reduce((sum, c) => sum + c.totalSpent, 0) / customers.length 
        : 0
    };
  }, [customers]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement des clients...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="customers-container">
        <div className="customers-header">
          <h1>Gestion des Clients</h1>
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="customers-stats">
          <div className="stat-card">
            <i className="fas fa-users"></i>
            <div className="stat-content">
              <h3>Total Clients</h3>
              <p>{stats.totalCustomers}</p>
            </div>
          </div>

          <div className="stat-card">
            <i className="fas fa-user-check"></i>
            <div className="stat-content">
              <h3>Clients Actifs</h3>
              <p>{stats.activeCustomers}</p>
            </div>
          </div>

          <div className="stat-card">
            <i className="fas fa-money-bill-wave"></i>
            <div className="stat-content">
              <h3>Revenu Total</h3>
              <p>{stats.totalRevenue.toFixed(2)} DZD</p>
            </div>
          </div>

          <div className="stat-card">
            <i className="fas fa-chart-line"></i>
            <div className="stat-content">
              <h3>Panier Moyen</h3>
              <p>{stats.averageOrderValue.toFixed(2)} DZD</p>
            </div>
          </div>
        </div>

        <div className="customers-table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Contact</th>
                <th>Localisation</th>
                <th>Commandes</th>
                <th>Total Dépensé</th>
                <th>Dernière Commande</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => (
                <tr key={customer.phone}>
                  <td>
                    <div className="customer-name">
                      <span className="avatar">
                        {customer.firstName[0]}{customer.lastName[0]}
                      </span>
                      <div>
                        <strong>{customer.firstName} {customer.lastName}</strong>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="customer-contact">
                      <i className="fas fa-phone"></i>
                      {customer.phone}
                    </div>
                  </td>
                  <td>
                    <div className="customer-location">
                      <div className="wilaya">{customer.wilaya}</div>
                      <div className="city">{customer.city}</div>
                    </div>
                  </td>
                  <td>
                    <div className="orders-count">
                      <span className="number">{customer.orders.length}</span>
                      <span className="label">commandes</span>
                    </div>
                  </td>
                  <td>
                    <div className="total-spent">
                      {customer.totalSpent.toFixed(2)} DZD
                    </div>
                  </td>
                  <td>
                    <div className="last-order">
                      {customer.lastOrderDate.toLocaleDateString()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
} 